import * as R from 'ramda';
import { CustomMenuItemProps, createMenu } from '../../components/Menu/CustomMenu';
import { FeatureFlagEnum } from '../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import LangEnum from '../../lang/LangEnum';
import PATHS from '../../routes/routes.const';
import useSystemParametersStore from '../../stores/SystemParameters/useSystemParameters.store';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../translation/TranslationContext';
import useUserContextGroupAbility from '../user-context-groups/hooks/useUserContextGroupAbility';
import { useAbility } from '../users/AbilityContext';
import { useStorefrontPathname } from './hooks/useStorefrontPathname';

const { settings } = PATHS;

export const useSettingMenuItems = () => {
  const ability = useAbility();
  const { translate } = useTranslation();
  const { isBrand, isAdminBrand } = useApplicationContext();
  const { getSystemParameterActive } = useSystemParametersStore();
  const canUserContextGroup = useUserContextGroupAbility();
  const { isProductMatchEnabled, isLocalsEnabled } = useFeatureFlag();

  const isSaleTargetsEnabled = getSystemParameterActive(FeatureFlagEnum.ENABLE_SALES_TARGET, true);
  const usersResourceName = isBrand ? 'brands-users' : 'stores-users';
  const storefrontPathname = useStorefrontPathname() ?? '/';

  const settingsMenuItems: CustomMenuItemProps[] = [
    {
      label: LangEnum.GLOBAL_ACCOUNT,
      group: true,
      items: [
        {
          label: LangEnum.MAIN_DRAWER_MY_ACCOUNT,
          name: 'profile',
          pathname: settings.profile.path,
          active: ability.can('menu', 'profile'),
        },
      ],
    },
    {
      label: LangEnum.GLOBAL_TEAM,
      group: true,
      items: [
        {
          label: LangEnum.GLOBAL_USERS,
          name: usersResourceName,
          pathname: settings.users.path,
          active: ability.can('menu', usersResourceName),
        },
        {
          label: LangEnum.USER_CONTEXT_GROUPS,
          name: 'user-context-groups',
          pathname: settings.userGroups.path,
          active: canUserContextGroup('menu'),
        },
        {
          label: LangEnum.GLOBAL_STOREFRONT,
          name: 'storefront',
          pathname: storefrontPathname,
          active: ability.can('menu', 'storefront'),
        },
        {
          label: LangEnum.GLOBAL_LICENSES,
          name: 'brands-licenses',
          pathname: settings.licenses.path,
          active: ability.can('menu', 'brands-licenses'),
        },
        {
          label: LangEnum.GLOBAL_REGISTRATION,
          name: 'stores',
          pathname: settings.store.path,
          active: ability.can('menu', 'stores'),
        },
        {
          label: LangEnum.GLOBAL_REGISTRATION,
          name: 'brands',
          pathname: settings.brand.path,
          active: ability.can('menu', 'brands'),
        },
        {
          label: LangEnum.GLOBAL_SALES_REPRESENTATIVES,
          name: 'sales-representatives',
          pathname: settings.salesRepresentatives.path,
          active: ability.can('menu', 'sales-representatives'),
        },
        {
          label: LangEnum.GLOBAL_SALES_TARGETS,
          name: 'sales-targets',
          pathname: settings.salesTargets.path,
          active: ability.can('menu', 'sales-targets') && isSaleTargetsEnabled,
        },
        {
          label: LangEnum.GLOBAL_CONSULTANTS,
          name: 'consultants',
          pathname: settings.consultants.path,
          active: ability.can('menu', 'consultants'),
        },
      ],
    },
    {
      label: LangEnum.GLOBAL_PRODUCTS,
      group: true,
      items: [
        {
          label: LangEnum.GLOBAL_MERCADOLOGICAL_TREE,
          name: 'mercadological-tree',
          pathname: settings.mercadologicalTree.path,
          active: ability.can('menu', 'mercadological-tree') && isProductMatchEnabled,
        },
        {
          label: LangEnum.GLOBAL_PRODUCT_MATCH,
          name: 'product-matches',
          pathname: settings.productMatches.path,
          active: ability.can('menu', 'product-matches') && isProductMatchEnabled,
        },
        {
          label: LangEnum.GLOBAL_PRODUCT_REGISTRATION,
          name: 'product-registrations',
          pathname: settings.productRegistrations.path,
          active: ability.can('menu', 'product-registrations') && isProductMatchEnabled,
        },
        {
          label: LangEnum.GLOBAL_PRODUCTS,
          name: 'products',
          pathname: settings.products.path,
          active: ability.can('menu', 'products'),
        },
        {
          label: LangEnum.GLOBAL_SKUS,
          name: 'skus',
          pathname: settings.skus.path,
          active: ability.can('menu', 'skus'),
        },
        {
          label: LangEnum.GLOBAL_PRE_ORDER,
          name: 'page-pre-order',
          pathname: settings.preOrder.path,
          active: ability.can('menu', 'sku-availabilities'),
        },
        {
          label: LangEnum.GLOBAL_FUTURE_STOCK,
          name: 'future-stocks',
          pathname: settings.futureStock.path,
          active: ability.can('menu', 'future-stocks'),
        },
        {
          label: LangEnum.GLOBAL_MATERIAL_PRE_ORDER,
          name: 'materials',
          pathname: settings.materialPreOrder.path,
          active: ability.can('menu', 'materials'),
        },
        {
          label: LangEnum.GLOBAL_READY_FOR_DELIVERY,
          name: 'stock',
          pathname: settings.stock.path,
          active: ability.can('menu', 'stock'),
        },
        {
          label: LangEnum.GLOBAL_PACKS,
          name: 'product-color-pack-stocks',
          pathname: settings.packs.path,
          active: isAdminBrand && ability.can('menu', 'product-color-pack-stocks'),
        },
        {
          label: LangEnum.LOCALS,
          name: 'locals',
          pathname: settings.locals.path,
          active: ability.can('menu', 'locals') && isLocalsEnabled,
        },
        {
          label: LangEnum.SHIPPING_COMPANIES,
          name: 'shipping-companies',
          pathname: settings.shippingCompanies.path,
          active: ability.can('menu', 'shipping-companies'),
        },
        {
          label: LangEnum.GLOBAL_PRODUCT_KITS,
          name: 'kits',
          pathname: settings.kits.path,
          active: ability.can('menu', 'kits'),
        },
        {
          label: LangEnum.MATERIAL_REGISTER,
          name: 'materials',
          pathname: settings.materials.path,
          active: ability.can('menu', 'materials'),
        },
        {
          label: LangEnum.MATERIAL_STOCK_MENU,
          name: 'materials',
          pathname: settings.materialStock.path,
          active: ability.can('menu', 'materials'),
        },
        {
          label: LangEnum.GLOBAL_COLORS,
          name: 'colors',
          pathname: settings.colors.path,
          active: ability.can('menu', 'colors'),
        },
        {
          label: LangEnum.GLOBAL_SIZES,
          name: 'sizes',
          pathname: settings.sizes.path,
          active: ability.can('menu', 'sizes'),
        },
        {
          label: LangEnum.GLOBAL_GRIDS,
          name: 'grids',
          pathname: settings.grids.path,
          active: ability.can('menu', 'grids'),
        },
        {
          label: LangEnum.GLOBAL_CATEGORIES,
          name: 'categories',
          pathname: settings.categories.path,
          active: ability.can('menu', 'categories'),
        },
        {
          label: LangEnum.GLOBAL_COLLECTIONS,
          name: 'collections',
          pathname: settings.collections.path,
          active: ability.can('menu', 'collections'),
        },
        {
          label: LangEnum.GLOBAL_DELIVERY_WINDOWS,
          name: 'delivery-windows',
          pathname: settings.deliveryWindows.path,
          active: ability.can('menu', 'delivery-windows'),
        },
        {
          label: LangEnum.GLOBAL_PRODUCTS_CLASSIFICATIONS,
          name: 'classifications',
          pathname: settings.classifications.path,
          active: ability.can('menu', 'classifications'),
        },
      ],
    },
    {
      label: LangEnum.CUSTOMERS,
      group: true,
      items: [
        {
          label: LangEnum.GLOBAL_CUSTOMERS_GROUPS,
          name: 'customer-groups',
          pathname: settings.customerGroups.path,
          active: ability.can('menu', 'customer-groups'),
        },
        {
          label: LangEnum.CUSTOMER_PRICE_TABLES_FORM_INPUT_LABEL,
          name: 'price-tables',
          pathname: settings.priceTables.path,
          active: ability.can('menu', 'price-tables'),
        },
        {
          label: LangEnum.CUSTOMER_PAYMENT_METHODS_ADD_MODAL_TITLE,
          name: 'payment-methods',
          pathname: settings.paymentMethods.path,
          active: ability.can('menu', 'payment-methods'),
        },
        {
          label: LangEnum.GLOBAL_COMMERCIAL_POLICIES,
          name: 'commercial-policy',
          pathname: settings.commercialPolicies.path,
          active: ability.can('menu', 'commercial-policy'),
        },
        {
          label: LangEnum.GLOBAL_BUSINESS_POLICIES,
          name: 'business-policies',
          pathname: settings.businessPolicies.path,
          active:
            ability.can('menu', 'business-policies') &&
            getSystemParameterActive(FeatureFlagEnum.ENABLE_BUSINESS_POLICIES),
        },
        {
          label: LangEnum.GLOBAL_CUSTOMERS_CLASSIFICATIONS,
          name: 'customer-classifications',
          pathname: settings.classificationsCustomer.path,
          active: ability.can('menu', 'customer-classifications'),
        },
      ],
    },
    {
      label: LangEnum.GLOBAL_MARKETING,
      group: true,
      items: [
        {
          label: LangEnum.GLOBAL_PROMOTIONS,
          name: 'promotions',
          pathname: settings.promotions.path,
          active: ability.can('menu', 'promotions'),
        },
      ],
    },
    {
      label: LangEnum.CUSTOMERS_TABS_RULES,
      group: true,
      items: [
        {
          label: LangEnum.GLOBAL_GENERAL_DEFINITIONS,
          name: 'brands-general-settings',
          pathname: settings.generalSettings.path,
          active: ability.can('menu', 'brands-general-settings'),
        },
        {
          label: LangEnum.GLOBAL_FILE_IMPORTS,
          name: 'imports',
          pathname: settings.imports.path,
          active: ability.can('menu', 'imports'),
        },
        {
          label: LangEnum.LIGHTDASH_REPORTS_PERMISSIONS,
          name: 'lightdash-reports',
          pathname: settings.reportPermissions.path,
          active:
            ability.can('menu', 'lightdash-reports') &&
            getSystemParameterActive(FeatureFlagEnum.ENABLE_LIGHTDASH_REPORTS, false),
        },
      ],
    },
    {
      label: LangEnum.GLOBAL_INTEGRATIONS,
      group: true,
      items: [
        {
          label: LangEnum.GLOBAL_ERP_SYSTEM,
          name: 'integrations',
          pathname: settings.integrations.path,
          active: ability.can('menu', 'integrations'),
        },
        {
          label: LangEnum.GLOBAL_TMS_SYSTEM,
          name: 'tms-integration-configs',
          pathname: settings.tmsIntegrations.path,
          active: ability.can('menu', 'tms-integration-configs'),
        },
        {
          label: LangEnum.GLOBAL_PAYMENT_GATEWAY,
          name: 'payment-gateways',
          pathname: settings.paymentGatewayIntegrations.path,
          active: ability.can('menu', 'payment-gateways'),
        },
        {
          label: LangEnum.GLOBAL_WEBHOOK_INTEGRATION,
          name: 'integrations-webhooks',
          pathname: settings.webhooks.path,
          active: ability.can('menu', 'integrations-webhooks'),
        },
        {
          label: LangEnum.STORE_INTEGRATION_CONFIG_MENU_LABEL,
          name: 'store-integration-config',
          pathname: settings.storeIntegrationConfig.path,
          active: ability.can('menu', 'store-integration-config'),
        },
      ],
    },
  ];

  const translatedSettingsMenuItems = settingsMenuItems.map((settingMenuItem) => ({
    ...settingMenuItem,
    label: translate(settingMenuItem.label as LangEnum),
    items: settingMenuItem.items
      .filter((item) => item.active || R.isNil(item.active))
      .map((settingSubmenuItem) => ({
        ...settingSubmenuItem,
        label: translate(settingSubmenuItem.label as LangEnum, {
          quantity: 1,
        }),
      })),
  }));

  return {
    settingsMenuItems,
    translatedSettingsMenuItems,
    settingsMenu: createMenu(translatedSettingsMenuItems, ability),
  };
};
